import React from "react"
import { makeAutoObservable } from "mobx"
import { t } from "@lingui/macro"

import { FormFields } from "src/lib/form-fields"
import {
    InfoCenterAdminService,
    infocenter_InformationItem,
    infocenter_InformationItemTypeEnum,
    ChatbotAdminService,
    config_ConfigDefinitionItem,
} from "src/api"
import { Channel } from "src/channel"
import { reportError } from "src/lib/report"
import { persistFiles } from "src/lib/file"
import { ILibraryInformationItem } from "src/types/library"
import { ISelectedAccessGroup } from "src/types/local-storage"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { ItemTypes } from "src/types/library-information-items/item-types"
import { ILibraryCategoryFlat } from "src/modals/library-information-items/types/libraryCategoryFlat"
import { ILibraryInformationItemFormFields } from "src/modals/library-information-items/types/formFields"
import { shouldPublishSegments } from "src/helpers/shouldPublishSegments"
import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"

export class LibraryInformationItemStore {
    static Context = React.createContext<LibraryInformationItemStore | null>(
        null,
    )
    formFields = new FormFields<ILibraryInformationItemFormFields>({})
    categories: ILibraryCategoryFlat[] = []
    itemTypes: ItemTypes[] = []
    isLoading = false
    accessType: string = ""
    accessGroups: ISelectedAccessGroup[] = []
    accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    isEditMode = false
    agentAccessTypes: IGenericAutoCompleteTextFieldValue[] = []
    agentDefinitions: config_ConfigDefinitionItem[] = []
    agentInstances: Map<string, IGenericAutoCompleteTextFieldValue[]> =
        new Map()
    isLoadingAgentTypes = false

    constructor() {
        makeAutoObservable(this)
    }

    init = async (
        isSuperUser: boolean,
        informationItemOrId?: ILibraryInformationItem | number,
        isCopyMode: boolean = false,
    ) => {
        let informationItem: ILibraryInformationItem | undefined

        // If we received an ID, fetch the complete item data
        if (
            typeof informationItemOrId === "number" &&
            informationItemOrId > 0
        ) {
            try {
                informationItem = await this.fetchItemById(informationItemOrId)
            } catch (error) {
                reportError(t`global.load-data-fail`, error)
                return
            }
        } else if (
            informationItemOrId != null &&
            typeof informationItemOrId !== "number"
        ) {
            // If we received the full item object, use it directly
            informationItem = informationItemOrId
        }

        const isEditMode = !isCopyMode && informationItem?.id !== undefined

        this.formFields.init({
            category: informationItem?.category_ids?.[0],
            document:
                informationItem?.item_type === "document"
                    ? informationItem.content
                    : undefined,
            internalName: informationItem?.admin_name ?? "",
            url:
                informationItem?.item_type === "url"
                    ? informationItem.content
                    : undefined,
            nameInApp: informationItem?.display_name ?? "",
            publishedIn: isCopyMode ? [] : informationItem?.segment_ids ?? [],
            page:
                informationItem?.item_type === "page"
                    ? informationItem.content
                    : undefined,
            type: informationItem?.item_type ?? "",
            accessType: isCopyMode ? "" : informationItem?.access_type ?? "",
            accessGroupId:
                informationItem?.access_group_id ?? this.accessGroupId,
            showInApp: isEditMode ? informationItem?.show_in_app : !isSuperUser,
            includeInChatbot:
                isEditMode || isCopyMode
                    ? informationItem?.include_in_chatbot
                    : isSuperUser,
            tag_ids: informationItem?.tag_ids ?? [],
            dimensionIds: isCopyMode
                ? []
                : informationItem?.dimension_ids ?? [],
            agents: [],
            agent_instances: {},
        })
        this.isEditMode = isEditMode

        // Load agent types if needed for chatbot functionality
        try {
            await this.fetchAgentTypes()
        } catch (error) {
            // Continue with initialization even if agent types fail to load
            // Continue with initialization even if agent types fail to load
        }

        // After agent types are loaded, populate agent data for edit mode
        if (isEditMode && informationItem != null && !isCopyMode) {
            // First, fetch ALL agent instances (like Knowledge Items do)
            await this.fetchAllAgentInstances()

            // Then populate the form fields with existing connections
            this.formFields.set("agents", informationItem.config_type_ids ?? [])
            this.formFields.set(
                "agent_instances",
                this.convertFeatureConfigIDsToInstancesMap(
                    informationItem.feature_config_ids ?? [],
                ),
            )
        }
    }

    fetchItemById = async (id: number): Promise<ILibraryInformationItem> => {
        const response = await InfoCenterAdminService.infocenterGetItem({
            informationItemId: id,
        })

        return {
            ...response,
            id: response.information_item_id as number,
            rank: response.rank ?? 0,
        }
    }

    setCategories = (categories: ILibraryCategoryFlat[]) => {
        this.categories = categories
    }

    setAccessGroups = (accessGroups: ISelectedAccessGroup[]) => {
        this.accessGroups = accessGroups
    }

    setSelectedAccessGroupId = (accessGroupId: number) => {
        this.accessGroupId = accessGroupId
    }

    setItemTypes = (itemTypes: ItemTypes[]) => {
        this.itemTypes = itemTypes
    }
    createItem = async () => {
        const publishedIn = this.formFields.get("publishedIn")
        const dimensionIds = this.formFields.get("dimensionIds")
        const enabledInChatbot =
            this.formFields.get("includeInChatbot") ?? false
        let itemResponse: infocenter_InformationItem = {}

        try {
            // Create
            await this.formFields.catchErrors(async () => {
                itemResponse =
                    await InfoCenterAdminService.infocenterCreateItem({
                        request: await this.getRequestData(),
                    })
            })

            if (itemResponse.information_item_id != null) {
                // tags update
                await this.handleTagsSubmit(
                    itemResponse.information_item_id as number,
                )

                // agent connections update
                if (enabledInChatbot) {
                    await this.updateAgentConnections(
                        itemResponse.information_item_id as number,
                    )
                }

                // publish
                if (shouldPublishSegments(publishedIn)) {
                    await InfoCenterAdminService.infocenterPublish({
                        informationItemId:
                            itemResponse.information_item_id as number,
                        request: {
                            published_in: publishedIn,
                            dimension_ids: dimensionIds,
                        },
                    })
                }
            }

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "library-information-items",
                    action: "update",
                },
            })
        } catch (error) {
            reportError(t`edit-information-item-modal.save-item-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    updateItem = async (item: ILibraryInformationItem) => {
        const publishedIn = this.formFields.get("publishedIn")
        const dimensionIds = this.formFields.get("dimensionIds")

        try {
            // Update
            await this.formFields.catchErrors(async () => {
                await InfoCenterAdminService.infocenterUpdateItem({
                    informationItemId: item.information_item_id as number,
                    request: await this.getRequestData(),
                })
            })

            // set tags
            await this.handleTagsSubmit(item.information_item_id as number)

            // agent connections update
            await this.updateAgentConnections(
                item.information_item_id as number,
            )

            // Publish
            await InfoCenterAdminService.infocenterPublish({
                informationItemId: item.information_item_id as number,
                request: {
                    published_in: publishedIn,
                    dimension_ids: dimensionIds,
                },
            })

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "library-information-items",
                    action: "update",
                },
            })
        } catch (error) {
            reportError(t`edit-information-item-modal.save-item-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    updateTags = async (tag_ids: number[], informationItemId: number) => {
        try {
            await InfoCenterAdminService.infocenterTag({
                informationItemId,
                request: {
                    tag_ids,
                },
            })
            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "library-information-items",
                    action: "update",
                },
            })
        } catch (error) {
            reportError(t`edit-information-item-modal.save-item-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    handleTagsSubmit = async (information_item_id: number) => {
        const tags = this.formFields.get("tag_ids")
        const shouldUpdateTags = this.formFields.getIsFieldDirty("tag_ids")

        if (tags !== undefined && shouldUpdateTags) {
            await this.updateTags(tags, information_item_id)
        }
    }

    urlString = (url: string) => {
        let newUrl = ""
        if (url.startsWith("http://")) {
            newUrl = `https://${url.split("http//")[1]}`
        } else if (url.startsWith("https://")) {
            newUrl = url
        } else {
            newUrl = `https://${url}`
        }
        return newUrl
    }

    getContent = async (formData: ILibraryInformationItemFormFields) => {
        let content = ""

        if (formData.type === "document") {
            if (typeof formData.document === "string") {
                content = formData.document
            } else {
                try {
                    content =
                        (
                            await persistFiles(
                                [formData.document as IFile],
                                "document",
                            )
                        )[0].url ?? ""
                } catch (error) {
                    reportError(
                        t`edit-information-item-modal.upload-document-fail`,
                        error,
                    )
                }
            }
        } else if (formData.type === "url") {
            content = this.urlString(formData.url ?? "") ?? ""
        } else if (formData.type === "page") {
            content = formData.page ?? ""
        }

        return content
    }

    convertFeatureConfigIDsToInstancesMap = (featureConfigIDs: number[]) => {
        const instancesMap: Record<string, number[]> = {}

        // Safety check - if agent definitions aren't loaded yet, return empty map
        if (
            this.agentDefinitions == null ||
            this.agentDefinitions.length === 0
        ) {
            return instancesMap
        }

        // Group feature config IDs by their agent type
        featureConfigIDs.forEach((configID) => {
            // Find which agent definition this config belongs to
            // We need to look up the config ID in the agent instances we've loaded
            let foundAgentName: string | null = null

            // Search through our loaded agent instances to find which agent this config belongs to
            for (const [
                agentName,
                instances,
            ] of this.agentInstances.entries()) {
                if (instances.some((instance) => instance.id === configID)) {
                    foundAgentName = agentName
                    break
                }
            }

            if (foundAgentName != null) {
                if (instancesMap[foundAgentName] == null) {
                    instancesMap[foundAgentName] = []
                }
                instancesMap[foundAgentName].push(configID)
            }
        })
        return instancesMap
    }

    getRequestData = async () => ({
        access_group_id:
            this.formFields.data.accessGroupId ?? DEFAULT_ACCESS_GROUP.id,
        admin_name: this.formFields.data.internalName ?? "",
        category_ids:
            this.formFields.data.category != null
                ? [this.formFields.data.category]
                : [],
        content: await this.getContent(this.formFields.data),
        display_name: this.formFields.data.nameInApp ?? "",
        item_type: (this.formFields.data.type ??
            "") as infocenter_InformationItemTypeEnum,
        accessType: this.formFields.data.accessType,
        show_in_app: this.formFields.data.showInApp,
        include_in_chatbot: this.formFields.data.includeInChatbot,
    })

    validateRequiredFields = (requiredFields: string[]) => {
        this.formFields.clearErrors()

        requiredFields.forEach((field) => {
            const requiredField =
                field as keyof ILibraryInformationItemFormFields
            const formField = this.formFields.get(requiredField)

            if (
                formField == null ||
                formField === "" ||
                formField === "<p></p>\n"
            ) {
                this.formFields.setError(requiredField, t`errors.required`)
            } else {
                this.formFields.clearError(requiredField)
            }
        })
    }

    setIsLoading = (isLoading: boolean) => (this.isLoading = isLoading)
    setAccessTypes = (accessType: string) => {
        this.accessType = accessType
    }

    get categoryOptions() {
        return this.categories
            .filter((category) => category.categoryId != null)
            .map((category) => ({
                id: category.categoryId as number,
                label: category.displayName ?? "",
            }))
    }

    getSelectedCategory(categoryId: number | null | undefined) {
        if (categoryId == null || this.categories.length === 0) {
            return null
        }

        const foundCategory = this.categories.find(
            (category) => category.categoryId === categoryId,
        )

        return foundCategory != null
            ? {
                  id: foundCategory.categoryId as number,
                  label: foundCategory.displayName ?? "",
              }
            : null
    }

    getAccessGroupIdForPublishSection() {
        if (!this.isEditMode) {
            return this.accessGroupId != null
                ? this.accessGroupId
                : this.formFields.get("accessGroupId")
        } else {
            return this.formFields.get("accessGroupId")
        }
    }

    // Agent-related methods
    setIsLoadingAgentTypes = (loading: boolean) => {
        this.isLoadingAgentTypes = loading
    }

    setAgentAccessTypes = (types: IGenericAutoCompleteTextFieldValue[]) => {
        this.agentAccessTypes = types
    }

    setAgentDefinitions = (definitions: config_ConfigDefinitionItem[]) => {
        this.agentDefinitions = definitions
    }

    setAgentInstances = (
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ) => {
        this.agentInstances.set(agentName, instances)
    }

    getAgentTypeValue = (): IGenericAutoCompleteTextFieldValue[] => {
        const selectedIds = this.formFields.get("agents") ?? []
        return this.agentAccessTypes.filter((agent) =>
            selectedIds.includes(Number(agent.id)),
        )
    }

    setSelectedAgentTypes = async (
        agentTypes: IGenericAutoCompleteTextFieldValue[],
    ) => {
        const agentIds = agentTypes.map((agent) => Number(agent.id))
        this.formFields.set("agents", agentIds)

        // Load instances for newly selected agent types
        for (const agentType of agentTypes) {
            const agentDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === agentType.id,
            )
            if (
                agentDefinition?.name != null &&
                agentDefinition.name !== "" &&
                !this.agentInstances.has(agentDefinition.name)
            ) {
                await this.fetchAgentInstances(agentDefinition.name)
            }
        }
    }

    deselectAgentType = (agentTypeId: number) => {
        const currentAgents = this.getAgentTypeValue()
        const updatedAgents = currentAgents.filter(
            (agent) => Number(agent.id) !== agentTypeId,
        )
        this.formFields.set(
            "agents",
            updatedAgents.map((agent) => Number(agent.id)),
        )
    }

    getSelectedAgentTypesForInstances = (): config_ConfigDefinitionItem[] => {
        const selectedIds = this.formFields.get("agents") ?? []
        return this.agentDefinitions.filter((def) =>
            selectedIds.includes(def.config_definition_id ?? 0),
        )
    }

    getInstancesForAgentType = (
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[] => {
        return this.agentInstances.get(agentName) ?? []
    }

    getSelectedInstancesForAgentType = (
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[] => {
        const agentInstances = this.formFields.get("agent_instances") ?? {}
        const selectedIds = agentInstances[agentName] ?? []
        const availableInstances = this.getInstancesForAgentType(agentName)

        return selectedIds
            .map((id) => {
                if (id === -1) {
                    return { id: -1, label: "all-instances" }
                }
                return availableInstances.find((instance) => instance.id === id)
            })
            .filter(
                (instance): instance is IGenericAutoCompleteTextFieldValue =>
                    instance !== undefined,
            )
    }

    setSelectedInstancesForAgentType = (
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ) => {
        const agentInstances = this.formFields.get("agent_instances") ?? {}
        agentInstances[agentName] = instances.map((instance) =>
            Number(instance.id),
        )
        this.formFields.set("agent_instances", agentInstances)
    }

    hasExistingInstanceConfigurations = (): boolean => {
        const agentInstances = this.formFields.get("agent_instances") ?? {}
        return Object.keys(agentInstances).length > 0
    }

    fetchAgentTypes = async () => {
        try {
            this.setIsLoadingAgentTypes(true)
            const configDefinition =
                await ChatbotAdminService.getV1AdminChatbotAgentConfigConfigDefinition(
                    {
                        space: "knowledgebase",
                    },
                )

            if (
                configDefinition.definition !== null &&
                configDefinition.definition !== undefined &&
                configDefinition.definition.length > 0
            ) {
                // Store the full definitions for metadata access
                this.setAgentDefinitions(configDefinition.definition)

                // Process agent types and check for instance availability
                const validTypes: IGenericAutoCompleteTextFieldValue[] = []

                for (const field of configDefinition.definition) {
                    if (
                        field.config_definition_id === null ||
                        field.config_definition_id === undefined ||
                        field.config_definition_id <= 0
                    ) {
                        continue
                    }

                    // Check if this agent type requires instances
                    const requiresInstances =
                        (field.metadata as Record<string, unknown>)
                            ?.agent_instance_required === true

                    if (
                        requiresInstances === true &&
                        field.name !== null &&
                        field.name !== undefined &&
                        field.name !== ""
                    ) {
                        // Pre-fetch instances for this agent type
                        try {
                            const instancesResponse =
                                await InfoCenterAdminService.getV1AdminInfocenterInformationItemConnectableAgents(
                                    {
                                        agentType: field.name,
                                    },
                                )

                            // Only add to valid types if instances are available
                            if (
                                instancesResponse.configs !== null &&
                                instancesResponse.configs !== undefined &&
                                instancesResponse.configs.length > 0
                            ) {
                                validTypes.push({
                                    id: field.config_definition_id,
                                    label: field.name,
                                })

                                // Cache the instances for later use
                                const instances = instancesResponse.configs.map(
                                    (config) => ({
                                        id: config.feature_config_id ?? 0,
                                        label: config.name ?? "",
                                    }),
                                )
                                this.setAgentInstances(field.name, instances)
                            }
                            // If no instances available, don't add to valid types
                        } catch (error) {
                            // Log error but continue processing other agent types
                            // eslint-disable-next-line no-console
                            console.error(
                                `Failed to fetch instances for agent ${field.name}:`,
                                error,
                            )
                        }
                    } else {
                        // Agent doesn't require instances, always add it
                        validTypes.push({
                            id: field.config_definition_id,
                            label: field.name ?? "",
                        })
                    }
                }

                this.setAgentAccessTypes(validTypes)
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-agent-types-fail`, error)
        } finally {
            this.setIsLoadingAgentTypes(false)
        }
    }

    fetchAgentInstances = async (agentName: string) => {
        // Check if we already have cached instances from fetchAgentTypes
        const cachedInstances = this.agentInstances.get(agentName)
        if (
            cachedInstances !== null &&
            cachedInstances !== undefined &&
            cachedInstances.length > 0
        ) {
            // Use cached instances, no need to fetch again
            return
        }

        try {
            const response =
                await InfoCenterAdminService.getV1AdminInfocenterInformationItemConnectableAgents(
                    {
                        agentType: agentName,
                    },
                )

            const instances = (response.configs ?? []).map((config) => ({
                id:
                    config.feature_config_id !== null &&
                    config.feature_config_id !== undefined
                        ? config.feature_config_id
                        : 0,
                label: config.name ?? "",
            }))

            this.setAgentInstances(agentName, instances)

            // Auto-select single instance using the helper method
            if (this.shouldAutoSelectInstance(agentName)) {
                const currentAgentInstances =
                    this.formFields.get("agent_instances") ?? {}

                // Only auto-select if nothing is currently selected for this agent
                if (
                    currentAgentInstances[agentName] === undefined ||
                    currentAgentInstances[agentName]?.length === 0
                ) {
                    const autoSelectedInstanceId =
                        this.getAutoSelectedInstance(agentName)
                    if (autoSelectedInstanceId !== null) {
                        this.formFields.set("agent_instances", {
                            ...currentAgentInstances,
                            [agentName]: [autoSelectedInstanceId],
                        })
                    }
                }
            }
        } catch (error) {
            reportError(
                t`knowledge-item-modal.load-agent-instances-fail`,
                error,
            )
        }
    }

    fetchAllAgentInstances = async () => {
        if (
            this.agentDefinitions == null ||
            this.agentDefinitions.length === 0
        ) {
            return
        }

        const promises = this.agentDefinitions.map(async (definition) => {
            if (
                definition.name !== null &&
                definition.name !== undefined &&
                definition.name.length > 0
            ) {
                await this.fetchAgentInstances(definition.name)
            }
        })
        await Promise.all(promises)
    }

    // Helper methods for instance field behavior based on user requirements table
    shouldShowInstanceField = (agentName: string): boolean => {
        const instances = this.getInstancesForAgentType(agentName)
        // Show instance field only when there are multiple instances (2 or more)
        return instances.length > 1
    }

    shouldAutoSelectInstance = (agentName: string): boolean => {
        const instances = this.getInstancesForAgentType(agentName)
        // Auto-select when there's exactly one instance
        return instances.length === 1
    }

    getAutoSelectedInstance = (agentName: string): number | null => {
        const instances = this.getInstancesForAgentType(agentName)
        // Return the single instance ID, or null if not applicable
        return instances.length === 1 ? (instances[0].id as number) : null
    }

    updateAgentConnections = async (informationItemId: number) => {
        try {
            let configTypeIds: number[] = []
            let configIds: number[] = []

            // If chatbot is enabled, get the selected agents and instances
            if (this.formFields.get("includeInChatbot") === true) {
                const selectedAgentIds = this.formFields.get("agents") ?? []
                const agentInstances =
                    this.formFields.get("agent_instances") ?? {}

                // Add selected agent types
                configTypeIds.push(...selectedAgentIds)

                // Add selected instances
                Object.entries(agentInstances).forEach(
                    ([agentName, instanceIds]) => {
                        instanceIds.forEach((id) => {
                            if (id === -1) {
                                // -1 is the "all instances" marker - get all actual instance IDs
                                const allInstances =
                                    this.getInstancesForAgentType(agentName)
                                allInstances.forEach((instance) => {
                                    configIds.push(instance.id as number)
                                })
                            } else {
                                configIds.push(id)
                            }
                        })
                    },
                )
            }

            // Always call the API - either with connections (if enabled) or empty arrays (if disabled)
            await InfoCenterAdminService.putV1AdminInfocenterInformationItemConnectAgents(
                {
                    informationItemId,
                    request: {
                        config_type_ids: configTypeIds,
                        config_ids: configIds,
                    },
                },
            )
        } catch (error) {
            reportError(
                t`edit-information-item-modal.connect-agents-fail`,
                error,
            )
        }
    }
}
